import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Gift, BookOpen, Heart, Sparkles, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const FreebiesLanding = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFreebie, setSelectedFreebie] = useState('both');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !name) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);

    try {
      // Send to backend to store in database
      const response = await axios.post(`${BACKEND_URL}/api/subscribers`, {
        name,
        email,
        freebie: selectedFreebie
      });

      if (response.data.success) {
        setSubmitted(true);
        if (response.data.already_subscribed) {
          toast.success('Welcome back!', {
            description: 'Your free resources are ready'
          });
        } else {
          toast.success('Welcome to your healing journey!', {
            description: 'Your free resources are ready'
          });
        }
      }
    } catch (error) {
      console.error('Subscription error:', error);
      // Fallback to localStorage if backend fails
      const subscriber = {
        name,
        email,
        freebie: selectedFreebie,
        subscribedAt: new Date().toISOString()
      };
      const subscribers = JSON.parse(localStorage.getItem('subscribers') || '[]');
      subscribers.push(subscriber);
      localStorage.setItem('subscribers', JSON.stringify(subscribers));
      
      setSubmitted(true);
      toast.success('Welcome to your healing journey!', {
        description: 'Your free resources are ready'
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="freebies-success" className="min-h-screen bg-cream py-12 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 bg-sage/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-fade-in">
            <CheckCircle className="w-10 h-10 text-sage" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-charcoal mb-4 animate-fade-in">
            Welcome, {name}!
          </h1>
          <p className="text-charcoal-light text-lg mb-10">
            Your free resources are ready. Click below to access them now.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <Link
              to="/free/chapter-1"
              data-testid="access-chapter1-btn"
              className="p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-all group"
            >
              <BookOpen className="w-8 h-8 text-sage mb-4 mx-auto" />
              <h3 className="font-heading text-xl text-charcoal group-hover:text-sage transition-colors">
                Chapter 1: The Fog of Trauma
              </h3>
              <p className="text-sm text-charcoal-light mt-2">
                Free preview with journal prompts
              </p>
              <span className="inline-flex items-center gap-1 text-sage text-sm mt-4">
                Read Now <ArrowRight className="w-4 h-4" />
              </span>
            </Link>

            <Link
              to="/free/5-lies"
              data-testid="access-5lies-btn"
              className="p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-all group"
            >
              <Heart className="w-8 h-8 text-gold mb-4 mx-auto" />
              <h3 className="font-heading text-xl text-charcoal group-hover:text-sage transition-colors">
                5 Lies Survivors Believe
              </h3>
              <p className="text-sm text-charcoal-light mt-2">
                And the truth that sets you free
              </p>
              <span className="inline-flex items-center gap-1 text-sage text-sm mt-4">
                Read Now <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>

          <div className="p-6 bg-gradient-to-r from-gold-light/30 to-sage-light/30 rounded-xl">
            <p className="text-charcoal mb-4">
              Ready for the complete transformation?
            </p>
            <Link
              to="/contents"
              data-testid="get-full-book-btn"
              className="inline-flex items-center gap-2 px-8 py-4 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all"
            >
              Get the Full Breakthrough Guide
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="freebies-landing" className="min-h-screen bg-cream">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ 
            backgroundImage: `url(https://images.unsplash.com/photo-1705905119187-c6986d4d44db?crop=entropy&cs=srgb&fm=jpg&q=85)` 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-cream/60 via-cream/80 to-cream" />
        
        <div className="relative max-w-4xl mx-auto px-6 pt-12 pb-16 md:pt-20 md:pb-24">
          <div className="text-center animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 rounded-full mb-6">
              <Gift className="w-4 h-4 text-gold" />
              <span className="text-sm font-medium text-gold">Free Resources</span>
            </div>
            
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-charcoal mb-4">
              Start Your Healing Journey
            </h1>
            <p className="text-lg md:text-xl text-charcoal-light max-w-2xl mx-auto">
              Get two powerful free resources to begin breaking free from the lies of trauma
            </p>
          </div>
        </div>
      </div>

      {/* Freebies Preview */}
      <div className="max-w-5xl mx-auto px-6 -mt-8">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Chapter 1 Preview */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden animate-slide-up">
            <div className="h-40 bg-gradient-to-br from-sage to-sage-dark flex items-center justify-center">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/98f4c0a7-37e3-4eee-b45d-3c901b2920ec/images/0470b11727d32ba005664c3bc8b17f0ec5bd3d699a6a10c5afd06c63414fb5d3.png"
                alt="Breakthrough Ebook Cover"
                className="h-36 rounded shadow-lg"
              />
            </div>
            <div className="p-6">
              <span className="text-xs font-medium text-gold uppercase tracking-wider">
                Free Preview
              </span>
              <h3 className="font-heading text-2xl text-charcoal mt-1 mb-2">
                Chapter 1: The Fog of Trauma
              </h3>
              <p className="text-charcoal-light text-sm mb-4">
                Begin your breakthrough journey with the complete first chapter, including scripture, 
                reflection content, and interactive journal prompts.
              </p>
              <ul className="space-y-2 text-sm text-charcoal-light">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-sage" />
                  Full chapter content
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-sage" />
                  3 guided journal prompts
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-sage" />
                  Opening prayer & affirmation
                </li>
              </ul>
            </div>
          </div>

          {/* 5 Lies Guide */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden animate-slide-up" style={{ animationDelay: '100ms' }}>
            <div className="h-40 bg-gradient-to-br from-gold to-gold/80 flex items-center justify-center">
              <Heart className="w-16 h-16 text-white/80" />
            </div>
            <div className="p-6">
              <span className="text-xs font-medium text-sage uppercase tracking-wider">
                Mini Guide
              </span>
              <h3 className="font-heading text-2xl text-charcoal mt-1 mb-2">
                5 Lies Survivors Believe
              </h3>
              <p className="text-charcoal-light text-sm mb-4">
                Expose the lies trauma planted in your heart and replace them with 
                God's liberating truth and healing scripture.
              </p>
              <ul className="space-y-2 text-sm text-charcoal-light">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-gold" />
                  5 common lies exposed
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-gold" />
                  Truth declarations with scripture
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-gold" />
                  Reflection prompts for each lie
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Email Capture Form */}
      <div className="max-w-xl mx-auto px-6 py-16">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
          <div className="text-center mb-8">
            <Sparkles className="w-10 h-10 text-gold mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-charcoal mb-2">
              Get Your Free Resources
            </h2>
            <p className="text-charcoal-light text-sm">
              Enter your details below for instant access
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-charcoal mb-2">
                Your First Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="freebie-name-input"
                placeholder="Enter your name"
                className="w-full px-4 py-3 bg-cream-paper rounded-lg border-none focus:ring-2 focus:ring-sage/30 placeholder:text-charcoal-light/50"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-charcoal mb-2">
                Your Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="freebie-email-input"
                placeholder="you@example.com"
                className="w-full px-4 py-3 bg-cream-paper rounded-lg border-none focus:ring-2 focus:ring-sage/30 placeholder:text-charcoal-light/50"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="freebie-submit-btn"
              className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>Processing...</>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  Send Me the Free Resources
                </>
              )}
            </button>

            <p className="text-xs text-center text-charcoal-light">
              Your information is safe. We never share or spam.
            </p>
          </form>
        </div>
      </div>

      {/* Social Proof / Testimonial Placeholder */}
      <div className="max-w-3xl mx-auto px-6 pb-16">
        <div className="text-center p-8 bg-gradient-to-r from-sky-light/30 to-sage-light/20 rounded-2xl">
          <p className="font-heading text-xl italic text-charcoal mb-4">
            "This guide gave me permission to grieve and hope at the same time. 
            For the first time, I believe healing is possible."
          </p>
          <p className="text-sm text-charcoal-light">— A Sister on the Journey</p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white/50 border-t border-stone-100 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Link to="/" className="font-accent text-2xl text-sage hover:text-sage-dark transition-colors">
            Breakthrough
          </Link>
          <p className="text-sm text-charcoal-light mt-2">A Faith-Based Guide to Healing</p>
          <p className="text-sm text-charcoal-light mt-1">By Janelle McGowan-Charles</p>
          <p className="text-xs text-charcoal-light/70 mt-1">Purpose Reclaimed Coaching</p>
          <p className="text-xs text-charcoal-light/60 mt-3">© {new Date().getFullYear()} Janelle McGowan-Charles, Purpose Reclaimed Coaching. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
